import { useCallback, useMemo, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import styled from 'styled-components';
import {
  MenuCollapse,
  MenuExpand,
  Pipeline,
  Settings,
} from '@elliemae/ds-icons';
import { PipelineShellContext } from './PipelineShellContext';

// Survives Layout remounts so the badge stays visible on other pages.
let lastFailedCount = 0;

const COLLAPSE_KEY = 'ppe.pipelineNav.collapsed';

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch (error) {
    return false;
  }
};

const Shell = styled.div`
  display: flex;
  align-items: stretch;
  min-height: calc(100vh - 100px);
  margin: 0 -${({ theme }) => theme.space.xs};
`;

const Nav = styled.nav<{ $collapsed: boolean }>`
  flex: none;
  width: ${({ $collapsed }) => ($collapsed ? '48px' : '220px')};
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutral['000']};
  border-right: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  padding: 8px 0;
`;

const Section = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.neutral[500]};
  padding: 14px 16px 6px;
`;

const Item = styled(NavLink)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 12px 0 13px;
  font-size: 13px;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.neutral[700]};
  border-left: 3px solid transparent;
  svg {
    flex: none;
  }
  &:hover {
    background: ${({ theme }) => theme.colors.neutral['050']};
  }
  &.active {
    background: ${({ theme }) => theme.colors.brand[200]};
    border-left-color: ${({ theme }) => theme.colors.brand[600]};
    color: ${({ theme }) => theme.colors.brand[700]};
    font-weight: 600;
  }
`;

const Label = styled.span`
  flex: 1;
  white-space: nowrap;
`;

const Badge = styled.span`
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.danger[900]};
  color: ${({ theme }) => theme.colors.neutral['000']};
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Dot = styled.span`
  position: absolute;
  top: 6px;
  right: 8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.danger[900]};
`;

const Spacer = styled.div`
  flex: 1;
`;

const Toggle = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 14px;
  border: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral[100]};
  background: none;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.neutral[500]};
  &:hover {
    background: ${({ theme }) => theme.colors.neutral['050']};
  }
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: 0 16px;
`;

export const PipelineShell = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [failedCount, setFailedCount] = useState(lastFailedCount);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, String(!prev));
      } catch (error) {
        // Persisting is best effort.
      }
      return !prev;
    });
  }, []);

  const context = useMemo(
    () => ({
      setFailedCount: (count: number) => {
        lastFailedCount = count;
        setFailedCount(count);
      },
    }),
    [],
  );
  const ToggleIcon = collapsed ? MenuExpand : MenuCollapse;

  return (
    <PipelineShellContext.Provider value={context}>
      <Shell>
        <Nav $collapsed={collapsed} aria-label="Rate sheets navigation">
          {!collapsed && <Section>RATE SHEETS</Section>}
          <Item to="/pipeline" title="Pipeline">
            <Pipeline size="s" />
            {!collapsed && <Label>Pipeline</Label>}
            {failedCount > 0 &&
              (collapsed ? (
                <Dot />
              ) : (
                <Badge title="Failed investors">{failedCount}</Badge>
              ))}
          </Item>
          <Item to="/rate-sheet-configuration" title="Rate sheet configuration">
            <Settings size="s" />
            {!collapsed && <Label>Rate sheet configuration</Label>}
          </Item>
          <Spacer />
          <Toggle
            type="button"
            onClick={toggle}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <ToggleIcon size="s" />
            {!collapsed && 'Collapse'}
          </Toggle>
        </Nav>
        <Content>
          <Outlet />
        </Content>
      </Shell>
    </PipelineShellContext.Provider>
  );
};
